import type { FieldRef, PickerValidDate } from '@mui/x-date-pickers/models';

/**
 * The ref a single-value picker's field hands back — the only way to see what is
 * actually TYPED in each section (`getSections()`): a field with just its hour
 * filled has no date to report, so `onChange` gets an invalid one instead.
 */
export type TimeFieldRef = FieldRef<PickerValidDate | null>;

/**
 * The 'HH:mm' a half-typed time field should settle on once focus leaves it, or
 * null when it must be left exactly as it is.
 *
 * Zeiterfassung times are overwhelmingly whole hours, so typing "7" and moving
 * on is how a row really gets filled in. Nothing beyond the minutes is ever
 * guessed: an empty field stays empty, and minutes the user typed are theirs.
 *
 * Blur is the right moment because MUI stops it propagating while focus merely
 * moves from one section to the next (see PickersSectionList) — never in the gap
 * between typing the hour and typing the minutes.
 */
export function completedTimeOnBlur(ref: TimeFieldRef | null): string | null {
    const sections = ref?.getSections();
    if (!sections) {
        return null;
    }
    // A meridiem or seconds section would make "HH:00" a guess about more than
    // the minutes — our fields have neither (ampm={false}), so this is a guard.
    if (
        sections.some(
            (section) =>
                section.type !== 'hours' &&
                section.type !== 'minutes' &&
                section.type !== 'empty'
        )
    ) {
        return null;
    }
    const hours = sections.find((section) => section.type === 'hours');
    const minutes = sections.find((section) => section.type === 'minutes');
    // Nothing typed at all, or already a complete time: not ours to touch.
    if (!hours?.value || minutes?.value) {
        return null;
    }
    const hour = Number(hours.value);
    if (!Number.isInteger(hour) || hour < 0 || hour > 23) {
        return null;
    }
    return `${String(hour).padStart(2, '0')}:00`;
}
